import React, { useState } from 'react';
import { AssetUrls } from 'config/assets';
import ManaFilters from 'components/ManaFilters/ManaFilters';
import SegmentedToggle from 'components/SegmentedToggle';
import ToggleSwitch from 'components/ToggleSwitch';
import InfoTooltip from 'components/InfoTooltip/InfoTooltip';
import { StatsDropdown } from './StatsDropdown';
import { Dropdown } from 'components/Dropdown/Dropdown';
import { SUPPORTED_LANGUAGES } from 'constants/languages';
import {
  ColorsMode,
  SearchFilterState,
  StatComparison,
  CmcComparison,
  normalizeColors,
} from '../searchState';
import PointsRangeControl from './PointsRangeControl';
import {
  ARRIVAL_COACHMARK_BODY,
  ARRIVAL_COACHMARK_HEADING,
  SEND_TOOLTIP_BODY,
  SEND_TOOLTIP_HEADING,
} from '../arrivalCoachmarkCopy';
import './FilterPanel.css';

interface FilterPanelProps {
  state: SearchFilterState;
  onChange: (patch: Partial<SearchFilterState>) => void;
  onClearAll: () => void;
  /** Larger pips and rows for the mobile sheet. */
  touch?: boolean;
  /**
   * The arrival coachmark is open, so every group EXCEPT Trade dims to direct
   * attention. Opacity only — nothing becomes pointer-events:none, because the
   * coachmark is non-blocking and the user must be able to keep filtering with
   * it open.
   */
  dimmed?: boolean;
  /**
   * Suppresses the "Available to receive" tooltip. Set while the coachmark is
   * open: it already carries this exact copy, and two overlapping explanations
   * of one control is a defect, not redundancy.
   */
  tooltipSuppressed?: boolean;
  /** Rendered inside the Trade group, below the toggle row: the coachmark. */
  tradeGroupSlot?: React.ReactNode;
}

/** Anchor for the mobile "Show me the filter" scroll-to. */
export const TRADE_GROUP_ID = 'filter-group-trade';

const COLOR_MODE_OPTIONS: Array<{ label: string; value: ColorsMode }> = [
  { label: 'Including', value: 'including' },
  { label: 'Exactly', value: 'exactly' },
  { label: 'At most', value: 'at_most' },
];

/** Desktop renders these as labelled chips; the mobile sheet keeps icon-only pips. */
const COLOR_CHIPS = [
  { key: 'W', label: 'White', icon: AssetUrls.search.manaSymbols.white },
  { key: 'U', label: 'Blue', icon: AssetUrls.search.manaSymbols.blue },
  { key: 'B', label: 'Black', icon: AssetUrls.search.manaSymbols.black },
  { key: 'R', label: 'Red', icon: AssetUrls.search.manaSymbols.red },
  { key: 'G', label: 'Green', icon: AssetUrls.search.manaSymbols.green },
  { key: 'C', label: 'Colorless', icon: AssetUrls.search.manaSymbols.colorless },
];

/** Lowercase to match the backend's format allowlist; the label is display-only. */
const FORMAT_OPTIONS = [
  'all formats',
  'alchemy',
  'brawl',
  'commander',
  'duel',
  'historic',
  'legacy',
  'modern',
  'oathbreaker',
  'pauper',
  'paupercommander',
  'penny',
  'pioneer',
  'predh',
  'premodern',
  'standard',
  'standardbrawl',
  'timeless',
  'vintage',
];

/* Symbol labels, not words: the comparison sits in a 116px track beside the
   value input, and "greater or equal" needs 129px there — it clipped to an
   ellipsis and read as unset. The symbol is also the notation the syntax
   parser accepts, so the two halves of the page agree. */
const CMC_COMPARISON_LABELS: Record<CmcComparison, string> = {
  eq: '= equal',
  gt: '> more',
  lt: '< less',
};

const STAT_COMPARISON_LABELS: Record<StatComparison, string> = {
  eq: '= equal',
  gt: '> more',
  gte: '≥ or more',
  lt: '< less',
  lte: '≤ or less',
};

const labelToKey = <T extends string>(labels: Record<T, string>, label: string, fallback: T): T =>
  (Object.keys(labels) as T[]).find((k) => labels[k] === label) ?? fallback;

const digitsOnly = (value: string) => value.replace(/\D/g, '');
/** Power and toughness can be negative on a few cards. */
const signedDigits = (value: string) => {
  const cleaned = value.replace(/[^\d-]/g, '');
  return cleaned === '-' ? cleaned : cleaned.replace(/(?!^)-/g, '');
};

const Section: React.FC<{
  title: string;
  children: React.ReactNode;
  /** Scroll anchor. Only the Trade group needs one today. */
  id?: string;
  /** Dimmed while the arrival coachmark points at a sibling group. */
  dimmed?: boolean;
  /** The group the coachmark points at: full opacity and the active treatment. */
  highlighted?: boolean;
}> = ({ title, children, id, dimmed, highlighted }) => (
  <section
    id={id}
    className={`filter-section${dimmed ? ' filter-section--dimmed' : ''}${
      highlighted ? ' filter-section--highlighted' : ''
    }`}
    data-testid="filter-section"
    data-dimmed={dimmed ? 'true' : undefined}
    data-highlighted={highlighted ? 'true' : undefined}
  >
    <h3 className="filter-section-title">{title}</h3>
    {children}
  </section>
);

/**
 * The filter panel. Section order is deliberate: colour identity is the most
 * recognizable and most-used structured filter, so it leads; the rarely-touched
 * fields sit in a collapsed group at the bottom.
 */
export const FilterPanel: React.FC<FilterPanelProps> = ({
  state,
  onChange,
  onClearAll,
  touch,
  dimmed,
  tooltipSuppressed,
  tradeGroupSlot,
}) => {
  const [moreOpen, setMoreOpen] = useState(
    // Open on load when something inside it is already set, so an inbound URL
    // never hides an active filter behind a collapsed header.
    // setName is deliberately absent: it is a top-level field now, so an
    // inbound URL that only sets it must not force this group open.
    Boolean(state.format || state.lang || state.artist || state.flavorText || state.reserved),
  );

  const toggleColor = (color: string) => {
    const next = state.colors.includes(color)
      ? state.colors.filter((c) => c !== color)
      : [...state.colors, color];
    onChange({ colors: normalizeColors(next) });
  };

  const languageOptions = ['Any language', ...SUPPORTED_LANGUAGES.map((l) => l.displayName)];
  const currentLanguage =
    SUPPORTED_LANGUAGES.find((l) => l.backendCode === state.lang)?.displayName ?? 'Any language';

  return (
    <aside className={`filter-panel ${touch ? 'touch' : ''}`} aria-label="Filters">
      {/* The sheet has its own header and apply button, so the panel only draws
          its own on desktop. */}
      {!touch && (
        <div className="filter-panel-header">
          <span className="filter-panel-title">FILTERS</span>
          <button type="button" className="filter-panel-reset" onClick={onClearAll}>
            Reset
          </button>
        </div>
      )}

      {/* Availability leads: "can I actually move this card?" narrows the result
          set harder than any card attribute, so it sits above colour identity.

          "Available to send" was hidden 2026-08-06 because its predicate cost
          15.5s on production — an affordability chain evaluated once per catalog
          row. It is back (2026-08-27): the backend now serves the filter from a
          precomputed set (SendableCard, rebuilt every 15 minutes) rather than
          evaluating the chain per request. Same param, same semantics, same
          result population — only the cost changed. */}
      <Section title="Trade" id={TRADE_GROUP_ID} highlighted={dimmed}>
        <div className="filter-toggle-row">
          <span>Available to send</span>
          {/* Sibling of the span, never inside it — same reason as the receive
              row below: `.filter-toggle-row > span` is nowrap + ellipsis and
              would clip the trigger's icon.

              Not gated on tooltipSuppressed. That flag exists because the
              arrival coachmark repeats the receive copy verbatim; there is no
              send coachmark, so this tooltip has nothing to collide with. */}
          <InfoTooltip
            focusable
            variant="panel"
            placement="bottom"
            ariaLabel="What Available to send means"
            text={
              <>
                <p className="info-tooltip-heading">{SEND_TOOLTIP_HEADING}</p>
                <p className="info-tooltip-body">{SEND_TOOLTIP_BODY}</p>
              </>
            }
          />
          <ToggleSwitch
            checked={state.availableToSend}
            onChange={(checked) => onChange({ availableToSend: checked })}
            ariaLabel="Available to send"
          />
        </div>
        <div className="filter-toggle-row">
          <span>Available to receive</span>
          {/* Sibling of the span, never inside it: `.filter-toggle-row > span`
              is nowrap + ellipsis, which would clip the trigger's icon. The
              tooltip carries the coachmark's explanation minus the dismiss
              button, so the answer stays reachable however the user arrived. */}
          {!tooltipSuppressed && (
            <InfoTooltip
              focusable
              variant="panel"
              placement="bottom"
              ariaLabel="What Available to receive means"
              text={
                // <p>, not <span>: the tooltip's heading/body classes carry
                // vertical margins, which do nothing on an inline element — as
                // spans these ran together into one paragraph.
                <>
                  <p className="info-tooltip-heading">{ARRIVAL_COACHMARK_HEADING}</p>
                  <p className="info-tooltip-body">{ARRIVAL_COACHMARK_BODY}</p>
                </>
              }
            />
          )}
          <ToggleSwitch
            checked={state.availableToReceive}
            onChange={(checked) => onChange({ availableToReceive: checked })}
            ariaLabel="Available to receive"
          />
        </div>
        {tradeGroupSlot}
      </Section>

      <Section title="Color identity" dimmed={dimmed}>
        {touch ? (
          <ManaFilters
            variant="pips"
            size={52}
            selectedColors={state.colors}
            onColorToggle={toggleColor}
          />
        ) : (
          <div className="filter-color-grid" role="group" aria-label="Colors">
            {COLOR_CHIPS.map((color) => {
              const isSelected = state.colors.includes(color.key);
              return (
                <button
                  key={color.key}
                  type="button"
                  className={`filter-color-chip ${isSelected ? 'selected' : ''}`}
                  aria-pressed={isSelected}
                  onClick={() => toggleColor(color.key)}
                >
                  <img src={color.icon} alt="" aria-hidden="true" />
                  {color.label}
                </button>
              );
            })}
          </div>
        )}
        <SegmentedToggle<ColorsMode>
          options={COLOR_MODE_OPTIONS}
          value={state.colorsMode}
          onChange={(colorsMode) => onChange({ colorsMode })}
          ariaLabel="Color match mode"
        />
      </Section>

      <Section title="Card text" dimmed={dimmed}>
        <label className="filter-field">
          <span>Card name</span>
          <input
            type="text"
            value={state.name}
            placeholder="Any words in the name"
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </label>
        {/* Set sits directly under the card name rather than in the collapsed
            group: narrowing to a printing is a primary search move, not an
            occasional refinement. */}
        <label className="filter-field">
          <span>Set</span>
          <input
            type="text"
            value={state.setName}
            placeholder="Set or expansion name"
            onChange={(e) => onChange({ setName: e.target.value })}
          />
        </label>
        <label className="filter-field">
          <span>Rules text</span>
          <input
            type="text"
            value={state.oracleText}
            placeholder='e.g. "Draw a card"'
            onChange={(e) => onChange({ oracleText: e.target.value })}
          />
        </label>
        <label className="filter-field">
          <span>Type line</span>
          <input
            type="text"
            value={state.typeLine}
            placeholder='e.g. "Instant" or "Human"'
            onChange={(e) => onChange({ typeLine: e.target.value })}
          />
        </label>
        <label className="filter-field">
          <span>Mana cost</span>
          <input
            type="text"
            value={state.manaCost}
            placeholder="e.g. {U}{U}"
            onChange={(e) => onChange({ manaCost: e.target.value })}
          />
        </label>
      </Section>

      {/* Each row is label-above, then [96px comparison][flex value]. The
          dropdown's minWidth is the real width floor here — a CSS-only fix
          leaves the MUI Select sizing itself from its content and the row
          still overflows the 344px rail. */}
      <Section title="Numbers" dimmed={dimmed}>
        <div className="filter-field">
          <span>Mana value</span>
          <div className="filter-stat-row">
            <StatsDropdown
              options={Object.values(CMC_COMPARISON_LABELS)}
              value={CMC_COMPARISON_LABELS[state.cmcComparison]}
              onChange={(label) =>
                onChange({ cmcComparison: labelToKey(CMC_COMPARISON_LABELS, label, 'eq') })
              }
              minWidth={96}
            />
            <input
              type="text"
              inputMode="numeric"
              className="filter-stat-input"
              value={state.cmc}
              aria-label="Mana value"
              onChange={(e) => onChange({ cmc: digitsOnly(e.target.value) })}
            />
          </div>
        </div>

        <div className="filter-field">
          <span>Power</span>
          <div className="filter-stat-row">
            <StatsDropdown
              options={Object.values(STAT_COMPARISON_LABELS)}
              value={STAT_COMPARISON_LABELS[state.powerComparison]}
              onChange={(label) =>
                onChange({ powerComparison: labelToKey(STAT_COMPARISON_LABELS, label, 'eq') })
              }
              minWidth={96}
            />
            <input
              type="text"
              inputMode="numeric"
              className="filter-stat-input"
              value={state.power}
              aria-label="Power"
              onChange={(e) => onChange({ power: signedDigits(e.target.value) })}
            />
          </div>
        </div>

        <div className="filter-field">
          <span>Toughness</span>
          <div className="filter-stat-row">
            <StatsDropdown
              options={Object.values(STAT_COMPARISON_LABELS)}
              value={STAT_COMPARISON_LABELS[state.toughnessComparison]}
              onChange={(label) =>
                onChange({ toughnessComparison: labelToKey(STAT_COMPARISON_LABELS, label, 'eq') })
              }
              minWidth={96}
            />
            <input
              type="text"
              inputMode="numeric"
              className="filter-stat-input"
              value={state.toughness}
              aria-label="Toughness"
              onChange={(e) => onChange({ toughness: signedDigits(e.target.value) })}
            />
          </div>
        </div>
      </Section>

      <Section title="Value" dimmed={dimmed}>
        <label className="filter-field">
          <span>Point value</span>
          <PointsRangeControl
            min={state.pointsMin}
            max={state.pointsMax}
            onChange={onChange}
          />
        </label>
      </Section>

      <section className="filter-section">
        <button
          type="button"
          className="filter-section-toggle"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen((o) => !o)}
        >
          Format, artist, language, flavor text
          <span className={`filter-section-chevron ${moreOpen ? 'open' : ''}`} aria-hidden="true" />
        </button>

        {moreOpen && (
          <div className="filter-section-body">
            <label className="filter-field">
              <span>Format</span>
              <Dropdown
                options={FORMAT_OPTIONS}
                value={state.format || 'all formats'}
                onChange={(value) => onChange({ format: value === 'all formats' ? '' : value })}
              />
            </label>
            {state.format && (
              <label className="filter-field">
                <span>Legality</span>
                <Dropdown
                  options={['legal', 'not legal']}
                  value={state.formatLegality === 'legal' ? 'legal' : 'not legal'}
                  onChange={(value) =>
                    onChange({ formatLegality: value === 'legal' ? 'legal' : 'not_legal' })
                  }
                />
              </label>
            )}
            <label className="filter-field">
              <span>Artist</span>
              <input
                type="text"
                value={state.artist}
                placeholder="Artist name"
                onChange={(e) => onChange({ artist: e.target.value })}
              />
            </label>
            <label className="filter-field">
              <span>Flavor text</span>
              <input
                type="text"
                value={state.flavorText}
                placeholder="Any flavor text"
                onChange={(e) => onChange({ flavorText: e.target.value })}
              />
            </label>
            <label className="filter-field">
              <span>Language</span>
              <Dropdown
                options={languageOptions}
                value={currentLanguage}
                onChange={(value) => {
                  const match = SUPPORTED_LANGUAGES.find((l) => l.displayName === value);
                  onChange({ lang: match?.backendCode ?? '' });
                }}
              />
            </label>
            <div className="filter-toggle-row">
              <span>Reserved list only</span>
              <ToggleSwitch
                checked={state.reserved}
                onChange={(checked) => onChange({ reserved: checked })}
              />
            </div>
          </div>
        )}
      </section>

      {/* Reset lives in the panel header on desktop; the sheet keeps a
          full-width control because it has no header of its own. */}
      {touch && (
        <button type="button" className="filter-panel-clear" onClick={onClearAll}>
          Clear all filters
        </button>
      )}
    </aside>
  );
};

export default FilterPanel;
